import type { MouseEvent } from "react";
import { useCallback } from "react";
import themeGet from "@styled-system/theme-get";
import {
  Box,
  Button,
  Divider,
  Flex,
  H5,
  Icon,
  Small,
} from "orcs-design-system";
import { NavLink } from "react-router-dom";
import styled, { css } from "styled-components";

import PATHS, { url } from "src/consts/urlPaths";
import { useUserTeamCanvasViews } from "src/hooks/userPreferences";
import { teamIdsSearch } from "src/components/TeamCanvas/useUrlTeamIds";

const navLinkStyles = css`
  text-decoration: none !important;
  font-size: ${themeGet("fontSizes.1")};
  color: ${themeGet("colors.bodyText")};

  &.active {
    color: ${themeGet("colors.primary")};
  }
`;

const CanvasNavLink = styled(NavLink)`
  ${navLinkStyles}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${themeGet("space.xs")};
  padding: ${themeGet("space.s")};
  border-radius: ${themeGet("radii.2")};
  transition: ${themeGet("transition.transitionDefault")};
  width: 100%;

  &.active {
    background-color: ${themeGet("colors.primaryLightest")};
    color: ${themeGet("colors.primary")};
  }

  &:hover {
    background-color: ${themeGet("colors.hoverSurface")};
    color: ${themeGet("colors.primary")};
  }
`;

const SavedViewRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: ${themeGet("space.xs")};
  padding: ${themeGet("space.s")};
  border-radius: ${themeGet("radii.2")};
  transition: ${themeGet("transition.transitionDefault")};

  &:has(a.active) {
    background-color: ${themeGet("colors.primaryLightest")};
  }

  &:hover {
    background-color: ${themeGet("colors.hoverSurface")};
  }
`;

const SavedViewLink = styled(NavLink)`
  ${navLinkStyles}
  flex: 1;
  min-width: 0;
`;

const TeamCanvasContent = () => {
  const { views, deleteView } = useUserTeamCanvasViews();

  const onDeleteView = useCallback(
    (event: MouseEvent, viewId: string) => {
      event.preventDefault();
      event.stopPropagation();
      deleteView(viewId);
    },
    [deleteView]
  );

  return (
    <Box mb="r">
      <Flex alignItems="center" gap="xs">
        <Icon icon={["fas", "sitemap"]} color="primary" />
        <H5 fontWeight="bold" data-testid="heading">
          Team Canvas
        </H5>
      </Flex>

      <Box my="r">
        <Divider light />
      </Box>

      <CanvasNavLink exact to={url(PATHS.TEAM_CANVAS)}>
        <span>Open Team Canvas</span>
        <Icon icon={["fas", "plus"]} />
      </CanvasNavLink>

      <Box my="r">
        <Small color="greyDark" fontWeight="bold">
          Saved views
        </Small>
      </Box>

      {views.length === 0 && (
        <Small color="greyMedium">
          No saved views yet. Add teams to the canvas and use “Save view” to
          keep the selection here.
        </Small>
      )}

      <Flex flexDirection="column" gap="xs">
        {views.map((view) => (
          <SavedViewRow
            key={view.id}
            data-testid={`team-canvas-saved-view-${view.id}`}
          >
            <SavedViewLink
              to={`${url(PATHS.TEAM_CANVAS)}${teamIdsSearch(view.teamIds)}`}
            >
              {view.name}
            </SavedViewLink>
            <Button
              type="button"
              variant="ghost"
              small
              iconOnly
              ariaLabel={`Delete saved view ${view.name}`}
              onClick={(event) => onDeleteView(event, view.id)}
              data-testid={`team-canvas-delete-view-${view.id}`}
            >
              <Icon icon={["far", "trash-alt"]} />
            </Button>
          </SavedViewRow>
        ))}
      </Flex>
    </Box>
  );
};

export default TeamCanvasContent;
